import type {
    ExtensionDefinition,
    FrontendPlugin,
} from "@backstage/frontend-plugin-api";
import {
    ApiBlueprint,
    createApiFactory,
    createFrontendPlugin,
    discoveryApiRef,
    identityApiRef,
} from "@backstage/frontend-plugin-api";
import { EntityCardBlueprint } from "@backstage/plugin-catalog-react/alpha";
import { ReleaseStatusClient } from "@internal/backstage-plugin-kubernetes-common";

import { releaseStatusApiRef } from "./api";

/**
 * Registers the {@link releaseStatusApiRef} API factory, providing a
 * {@link ReleaseStatusClient} backed by the Backstage discovery and identity APIs.
 */
const releaseStatusApi: ExtensionDefinition = ApiBlueprint.make({
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: releaseStatusApiRef,
                deps: {
                    discoveryApi: discoveryApiRef,
                    identityApi: identityApiRef,
                },
                factory: ({ discoveryApi, identityApi }) =>
                    new ReleaseStatusClient({ discoveryApi, identityApi }),
            })
        ),
});

/**
 * Overview card for Component entity pages: compares the highest-priority reachable
 * Kubernetes deployment (primary env: prd > stg > dev) against the entity's latest GitHub release.
 * Gated to Component entities in code.
 *
 * The loader uses a relative import (rather than importing this package's own
 * public barrel) to avoid a self-import and to keep the card on its own
 * code-split chunk.
 */
const releaseStatusCard = EntityCardBlueprint.make({
    name: "release-status",
    params: {
        filter: (entity: { kind: string }) =>
            entity.kind.toLowerCase() === "component",
        loader: () =>
            import("./components/ReleaseStatusCard/ReleaseStatusCard").then(
                m => <m.ReleaseStatusCard />
            ),
    },
});

/**
 * The Kubernetes release-status frontend plugin.
 *
 * Registers the {@link releaseStatusApiRef} API and the release-status entity
 * card. All data aggregation is performed by the `kubernetes-backend-module-deployments`
 * backend module.
 *
 * @public
 */
export const kubernetesPlugin: FrontendPlugin = createFrontendPlugin({
    pluginId: "k8s-release-status",
    extensions: [releaseStatusApi, releaseStatusCard],
});

export default kubernetesPlugin;
